export interface PlansResponse {
    title: string;
    message: string;
    data: PlansBundle;
}

export interface PlansBundle {
    plansToImprove: Plan[];
    plans: Plan[];
    currentPlan: Plan;
    nextPayment: NextPayment;
    paidDates: PaidDates[];
    billsplan: Billplan[];
    currentBillplan: Billplan;
    rejectedCurrentBill: Billplan;
    isInProcess: boolean;
    /** True si hay un cambio mid-cycle (plan/frecuencia) sin resolver. */
    hasUnresolvedMidCycleChange?: boolean;
    paymentFrequency?: string;
    purchasedUsers?: number;
    extendUsers?: number;
    seats?: SeatsSummary;
    // Nuevos campos para saldo a favor del cliente
    balanceInFavor?: number;
    statusTenant?: string;
    extraUserPriceUsd?: number;
    setupService?: {
        purchased?: boolean;
        canSell?: boolean;
        available?: boolean;
        baseAmountUSD?: number;
        concept?: string;
        /** IVA de Setup (0 si el tenant no es plus). Viene de GET /plans. */
        ivaUSD?: number;
        /** Retención sobre el IVA de Setup. Viene de GET /plans; el front no la recalcula. */
        retentionUSD?: number;
        /** baseAmountUSD + ivaUSD − retentionUSD. Cotización pagable de Setup. */
        payableUSD?: number;
    };
    creditInfo?: {
        currentBalance: number;
        hasCredit: boolean;
        billsWithDiscount: {
            _id: string;
            discountByCredit: number;
            paymentDatePlan: string;
        }[];
    };
    /**
     * Oferta del cupo fiscal extra (ADM-740). Ausente en un servidor sin ADM-740: se
     * trata como venta cerrada. Se lee solo con `normalizeFiscalOffer` (`fiscal-quota.util`).
     */
    fiscalOffer?: FiscalOffer;
    /**
     * Cupo extra vigente del comercio: `null` si el plan actual no es fiscal (aunque quede
     * un extra residual) o si la cantidad es 0.
     */
    currentExtraControlNumbers?: FiscalQuota | null;
    /**
     * El comercio ya puede facturar (ADM-828). Ausente: el front usa solo el permiso
     * del plan. Ese fallback es transitorio. `false` cierra el acceso.
     */
    fiscalReady?: boolean;
}

/** Cupo fiscal extra: facturas adicionales al cupo base del Plan Fiscal y su USD neto mensual. */
export interface FiscalQuota {
    quantity: number;
    monthlyAmountUSD: number;
}

/** `fiscalOffer` de `GET /api/plans`: las cinco claves vienen siempre. */
export interface FiscalOffer {
    /** `salesEnabled` y un único Plan Fiscal con `controlNumbers > 0`. Falla cerrado. */
    available: boolean;
    /** `String(_id)` del Plan Fiscal; viaja aunque `available` sea false. */
    planId: string | null;
    planType: string | null;
    /** Cupo base mensual del Plan Fiscal (`Plan.controlNumbers`). */
    includedQuantity: number | null;
    /** Ascendente. `quantity` = facturas adicionales; `amount` = USD neto mensual adicional. */
    tiers: { quantity: number; amount: number }[];
}

export interface Billplan {
    _id?: string;
    amount: number;
    amountPlan: number;
    /** Neto USD de la factura (`GET /plans/bills-plan`). En mix `amountPlan` va null. */
    baseAmount?: number;
    paidDate: string;
    paymentDatePlan: string;
    paid: boolean;
    extraPayments: ExtraPayments[];
    status: string;
    plus: number;
    reasonRejected?: string;
    retention: number;
    /** Retención en VES persistida por backend; no se recalcula en el cliente. */
    retentionVES?: number;
    tenantId: string;
    createdAt: Date;
    updatedAt: Date;
    // Nuevos campos para saldo a favor del cliente
    paidAmount?: number;
    discountByCredit?: number;
    remainingCreditAfterBill?: number;
    // Unificación de campos duplicados tras merge: mantener una sola definición
    paymentMethod?: PlansPaymentMethod; // Si antes solo se usaba name, sigue cubierto por este modelo extendido
    ref?: string;
    issuing?: string;
    /** Teléfono de quien pagó. Solo lo llena Débito Inmediato. */
    payerPhone?: string;
    comments?: string;
    amountVES?: number;
    exchangeRateBCV?: number;
    digitalInvoiceData?: {
        urlpdf?: string;
    };
    plan?: Pick<Plan, 'type'>;
    planType?: string;
    /**
     * Canje sellado en la factura (`apply-coupon`). Si viene y `consumedAt` es
     * null, el cobro lee este descuento: la UI no puede pintar precio lleno.
     */
    appliedCoupon?: {
        couponId?: string;
        code?: string;
        discountType?: PlanCouponDiscountType;
        value?: number;
        discountUSD?: number;
        planId?: string;
        paymentFrequency?: string;
        consumedAt?: string | Date | null;
        /** Fin del hold de 30 min. Ausente ≡ sello legacy (no caduca en el cliente). */
        reservedUntil?: string | Date | null;
    };
    /** Tipo de factura (`billPlan`, `sms`, …). Ausente ⇒ se asume `billPlan`. */
    paymentType?: string;
    includesTypes?: string[];
    specificData?: Array<{
        key?: string;
        valueStr?: string;
        valueNumber?: number;
    }>;
    bankValidationStatus?: string;
    bankValidationDetails?: {
        applicationStatus?: PaymentApplicationStatus | null;
        label?: string;
        summary?: string;
    };
}

export interface ExtraPayments {
    _id: string;
    name: string;
    amount: number;
    createdAt: Date;
    updatedAt: Date;
}
export interface Plan {
    _id: string;
    type: string;
    free_trial_month: number;
    amount: number;
    amountDiscount: number;
    permissions: PlansPermissions;
    createdAt: Date;
    updatedAt: Date;
    imgUrl?: string;
    description?: string;
    /** Asientos incluidos en el plan. */
    users?: number;
}

export interface PlansPermissions {
    POS: string[];
    INVENTORY: string[];
    BANK_ACCOUNTS: string[];
    FINANCIAL_SUMMARY: string[];
    MARKETING: string[];
    COMMERCE: string[];
    EXTRAS: string[];
    FISCAL_INVOICE?: string[];
}

export interface SeatsSummary {
    planUsers: number;
    extendUsers: number;
    purchasedUsers: number;
    included: number;
    total: number;
    createdUsers: number;
    unassigned: number;
}

export interface NextPayment {
    date: Date;
    dateToPay: Date;
    amount: number;
    type: string;
    paymentStatus: string;
    amountToPay?: number;
    graceDays?: number;
    isLifetime?: boolean;
}

export interface PaidDates {
    _id: string;
    paymentMethod: string;
    paymentDatePlan: Date;
    ref: string;
    amount: number;
    amountVES: number;
    exchangeRateBCV: number;
    paymentDate: Date;
    status: string;
    tenantId: string;
    createdAt: Date;
    updatedAt: Date;
}

export type BankAccountPlansPermissions = 'bank-accounts' | 'pending-accounts' | 'history';

export type CommercePlansPermissions = 'preferences' | 'users' | 'staff';

export type FinancialSummaryPlansPermissions = 'fixed-variable-expenses' | 'utility-loss';

export type InventoryPlansPermissions = 'inventory' | 'template-products' | 'registers';

export type POSPlansPermissions = 'direct-sales' | 'open-sales' | 'sales-channels' | 'sales-registries';

export type MarketingPlansPermissions = 'clients' | 'campaings';

export type ExtrasPlansPermissions = 'excel';

export type FiscalPlansPermissions = 'generate-fiscal-invoice';

export type AllPermissions =
    | BankAccountPlansPermissions
    | CommercePlansPermissions
    | FinancialSummaryPlansPermissions
    | InventoryPlansPermissions
    | POSPlansPermissions
    | MarketingPlansPermissions
    | ExtrasPlansPermissions
    | FiscalPlansPermissions;

export interface PlansPaymentMethod {
    _id: string;
    name: string;
    ownerName: string;
    email: string;
    phone: string;
    dni: string;
    currency: {
        name: string;
        code: string;
    };
    /** Alineado con `Commerce.plus`: método visible solo para tenants plus. */
    plus?: boolean;
    showToClient?: boolean;
    imgUrl?: string;
}

/**
 * Avisos para los comercios que la migración a facturación formal acaba de
 * volver jurídicos. Lo calcula el backend en `GET /plans/payment-methods`.
 */
export interface PlanMigrationNotice {
    /** Pasaron de pagar a la cuenta personal (Bancrecer) a la de la empresa (BNC). */
    bankAccountChanged: boolean;
    /** Ya son jurídicos pero no tenemos su RIF, obligatorio para facturar. */
    requiresFiscalInfo: boolean;
}

/** Datos fiscales que carga el comercio desde el modal de pago. */
export interface FiscalInfoRequest {
    taxIdType: string;
    taxIdNumber: string;
    address: string;
    phone?: string;
}

/** Outcomes del cobro con tarjeta: `POST /plans/pay-with-card` y su confirmación. */
export type CardChargeOutcome =
    | 'succeeded'
    | 'already_processed'
    | 'requires_action'
    /** Cobro aún en vuelo en el procesador; se confirma solo (webhook). */
    | 'processing'
    /** El procesador no respondió: ni éxito ni rechazo. El cargo pudo entrar. */
    | 'unknown'
    | 'failed'
    | 'requires_card_update';

/** Cobro que no cerró en línea: no hay éxito que reportar ni rechazo que anunciar. */
export type CardChargeUnresolvedOutcome = Extract<CardChargeOutcome, 'processing' | 'unknown'>;

/**
 * Error del cobro mix con tarjeta que NO es un rechazo (no emite `chargeFailed`):
 * `renewal_due` = 409 `EXTRA_USERS_MIX_RENEWAL_DUE`; `request_error` = cualquier otro fallo del request.
 */
export interface AutoPayCardMixChargeIssue {
    reason: 'renewal_due' | 'request_error';
    httpStatus: number | null;
}

/** Outcomes canónicos del pre-check `POST /plans/verify-payment`. */
export type PaymentVerificationOutcome =
    | 'verified'
    | 'not_found'
    | 'amount_mismatch'
    | 'amount_below_tolerance'
    | 'overpayment_within_tolerance'
    | 'overpayment_above_tolerance'
    | 'duplicate_reference'
    | 'invalid_expected_amount'
    | 'date_mismatch'
    | 'skipped_staging'
    | 'not_verifiable'
    | 'error';

export interface PaymentVerificationAlert {
    code: string;
    severity: 'info' | 'warning' | 'error';
    message: string;
}

export type PaymentApplicationStatus = 'not_applicable' | 'applied' | 'manual_review';

export interface VerifyPaymentRequest {
    paymentMethodId: string;
    reference: string;
    expectedAmount: number;
    expectedAmounts: { USD?: number; VES?: number };
    exchangeRateBCV?: number;
    expectedDate?: string;
    billPlanId?: string;
}

export interface VerifyPaymentResponseData {
    responseScope: 'public';
    reference: string;
    outcome: PaymentVerificationOutcome;
    verified: boolean;
    autoApprove: boolean;
    requiresManualReview: boolean;
    message: string;
    currency: 'USD' | 'VES' | null;
    expectedAmount: number | null;
    manualReviewAttempts: number;
    alerts: PaymentVerificationAlert[];
    applicationStatus?: PaymentApplicationStatus | null;
    applicationError?: string | null;
    label?: string;
    summary?: string;
}

export interface VerifyPaymentResponse {
    success: boolean;
    message: string;
    data: VerifyPaymentResponseData;
}

/**
 * Tipo de descuento del cupón. `specialPrice` FIJA el subtotal del período al
 * valor; `fixedAmount` lo RESTA (la semántica es contraintuitiva y el panel de
 * Comercial la describe al revés: bug preexistente de ADM-601). `percentage` es
 * legacy: no se puede crear desde el panel pero existe en BD.
 */
export type PlanCouponDiscountType = 'fixedAmount' | 'specialPrice' | 'percentage';

/** Cupón tal como lo devuelven `preview-coupon` y `apply-coupon` de api-admin. */
export interface PlanCouponSummary {
    code: string;
    discountType: PlanCouponDiscountType;
    value: number;
}

/**
 * Plan y frecuencia que el comercio tiene elegidos en el paso 1 y que todavía no
 * están en la factura. Ausentes, api-admin calcula el desglose con los de la
 * factura; presentes, valida el cupón y calcula la base contra la selección.
 */
export interface PlanCouponSelection {
    /** `_id` del plan seleccionado (mismo valor que `planType` en `/plans/pay`). */
    planType?: string | null;
    /** `mensual` | `trimestral` | `semestral` | `anual`. */
    paymentFrequency?: string | null;
}

// ── Débito Inmediato (BNC) ──

/** Cambio de plan/frecuencia que el cliente esté eligiendo al pagar. */
export interface DebitoInmediatoChangeOptions {
    planType?: string | null;
    paymentFrequency?: string | null;
    /** Cupos pedidos en este checkout (ADM-743). Solo con intención de cupos. */
    pendingPurchasedUsers?: number | null;
    /** Usuarios que se bloquean al confirmar el pago. Viajan solo junto al conteo. */
    pendingBlockUserIds?: string[] | null;
    /** Cupo fiscal extra pedido (ADM-740). Solo con intención; el mismo en quote, token y charge. */
    extraControlNumbers?: number | null;
}

/**
 * Cuenta con la que se paga: una guardada (solo su `id`, los datos los resuelve
 * el servidor) o una nueva. Nunca se mandan las dos cosas a la vez.
 */
export interface DebitoInmediatoAccountInput {
    savedInstrumentId?: string;
    phone?: string;
    cedula?: string;
    bankCode?: string;
    bankName?: string;
    holderName?: string;
}

/** Cuenta guardada tal como la devuelve la API: teléfono y cédula enmascarados. */
export interface DebitoInmediatoSavedAccount {
    id: string;
    phone: string;
    cedula: string;
    bankCode: string | null;
    bankName: string | null;
    holderName: string | null;
    addedAt: string | null;
    lastUsedAt: string | null;
    lastStatus: string | null;
}

export interface DebitoInmediatoBank {
    code: string;
    name: string;
}

/** Monto que cotiza el servidor. El navegador lo muestra, no lo calcula. */
export interface DebitoInmediatoQuote {
    amounts: { amountUSD: number; amountVES: number; exchangeRateBCV: number };
    /** `procesando` = el cliente ya reportó un pago para esta factura. */
    billStatus: string;
    isMidCycle: boolean;
    isPlanOnlyChange: boolean;
    planOnlyMonths: number | null;
    paymentFrequency: string;
    /** Cupos incluidos en el monto (ADM-743). Ausente en un servidor sin el hotfix. */
    extraUsersSeats?: number;
    /** Cupo fiscal extra incluido en el monto (ADM-740). Ausente en un servidor sin ADM-740. */
    extraControlNumbers?: number;
}

/**
 * Desglose de la factura calculado por api-admin. **Es la única fuente del
 * total con cupón**: el front no recalcula nada a partir de estos campos, solo
 * los pinta y los convierte a la moneda del método de pago.
 *
 * Orden fijo del servidor: base del período (ya con descuento de frecuencia)
 * → cupón → − saldo a favor → + IVA → − retención.
 */
export interface CouponDesglose {
    periodBaseUsd: number;
    couponDiscountUsd: number;
    creditDiscountUsd: number;
    /**
     * Base de los cupos facturados (ADM-743). Solo desglosa: ya va dentro de
     * `baseUsd`, `ivaUsd` y `totalUsd`, así que nunca se suma encima. Opcional: un
     * sello de un servidor sin el hotfix no lo trae (≡ 0).
     */
    extraUsersBaseUsd?: number;
    /**
     * Base del cupo fiscal extra facturado (ADM-740), desde las claves de la factura. Igual
     * que `extraUsersBaseUsd`: ya va dentro de `baseUsd`/`ivaUsd`/`totalUsd`. Ausente ≡ 0.
     */
    extraControlNumbersBaseUsd?: number;
    baseUsd: number;
    ivaUsd: number;
    retentionUsd: number;
    totalUsd: number;
}

/** 200 de `POST /plans/coupon/preview`. Un rechazo llega como 400/404/409. */
export interface CouponPreviewResponse {
    title: string;
    message: string;
    data: {
        valid: boolean;
        coupon?: PlanCouponSummary;
        desglose?: CouponDesglose;
    };
}

/** 200 de `POST /plans/coupon/apply`: sella el cupón en la factura. */
export interface CouponApplyResponse {
    title: string;
    message: string;
    data: {
        billPlanId: string;
        coupon: PlanCouponSummary;
        desglose: CouponDesglose;
        /** ISO del hold. El timer del checkout usa este valor, no un 30 min local. */
        reservedUntil?: string | Date | null;
    };
}

/** 409 de cobro cuando el hold de 30 min ya venció (api-admin unseala y rechaza). */
export const COUPON_HOLD_EXPIRED_TITLE = 'Cupón reservado expirado';
export const COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE = 'El tiempo para pagar con este cupón expiró. Puedes volver a aplicarlo si sigue disponible.';
